import { Component, OnInit, OnDestroy, HostListener, HostBinding, NgZone, ChangeDetectorRef, ViewChild, TemplateRef, ChangeDetectionStrategy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Observable, Subject, takeUntil, debounceTime, take, interval, fromEvent, merge, finalize, filter, map, startWith, distinctUntilChanged, skip, of, tap } from 'rxjs';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';
import { Editor, Toolbar } from 'ngx-editor';
import { TourGuideTooltipService } from '../../services/tour-guide-tooltip.service';
import { TooltipConfig } from '../../models/tooltip.model';
import { TooltipPosition, TooltipAlignment, TooltipIconMode, TooltipAlignTarget } from '../../models/tooltip.enum';
import { SimpleSelectOption } from '../simple-select/simple-select.component';
import { TooltipAnchorService, AnchorStability } from '../../services/tooltip-anchor.service';
import { TooltipReanchorService } from '../../services/tooltip-reanchor.service';
import { TooltipInjectorService } from '../../services/tooltip-injector.service';
import { AngularContextService } from '../../services/angular-context.service';
import { TooltipUtilsService, TooltipZIndexService } from '../../services/tooltip-utils.service';
import { TooltipUnsavedChangesService } from '../../services/tooltip-unsaved-changes.service';
import { stripHtml } from '../../_helpers/html.helper';
import { BORDER_WIDTH_MAX, BORDER_WIDTH_MIN, pagePath } from './tooltip-admin-toolbar.constants';

@Component({
  selector: 'app-tooltip-admin-toolbar',
  templateUrl: './tooltip-admin-toolbar.component.html',
  styleUrl: './tooltip-admin-toolbar.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class TooltipAdminToolbarComponent implements OnInit, OnDestroy {
  @ViewChild('templateDeleteConfirm') templateDeleteConfirm!: TemplateRef<any>;
  @ViewChild('templateConfirmSwitch') templateConfirmSwitch!: TemplateRef<any>;

  readonly TooltipPosition = TooltipPosition;
  readonly TooltipAlignment = TooltipAlignment;
  readonly TooltipIconMode = TooltipIconMode;
  readonly TooltipAlignTarget = TooltipAlignTarget;
  readonly AnchorStability = AnchorStability;
  readonly HEADER_MAX_LENGTH = 255;
  readonly TEXT_MAX_LENGTH = 1000;
  readonly BORDER_WIDTH_MIN = BORDER_WIDTH_MIN;
  readonly BORDER_WIDTH_MAX = BORDER_WIDTH_MAX;

  editor: Editor = new Editor();
  // List type (see setListType) and link (see insertLink) are picked from
  // custom dropdowns instead of ngx-editor's built-in items, so they aren't
  // in this toolbar — the built-in link popup requires display text, and we
  // want it optional.
  readonly textToolbar: Toolbar = [
    ['bold', 'italic', 'underline']
  ];
  linkForm = { href: '', text: '' };

  @HostBinding('class.panel-open') get panelOpenClass() { return this._isPanelVisible; }

  private _isPanelVisible = true;
  get isPanelVisible(): boolean { return this._isPanelVisible; }
  set isPanelVisible(val: boolean) {
    this._isPanelVisible = val;
    document.documentElement.style.setProperty('--tooltip-sidebar-width', val ? '380px' : '0px');
  }
  isPickingElement = false;
  tooltips: TooltipConfig[] = [];
  filteredTooltips: TooltipConfig[] = [];
  private domPresenceIds = new Set<string>();
  searchQuery = '';
  pickingField: 'icon' | 'container' | null = null;
  pickingStep: 1 | 2 | null = null;
  private pendingContainerElement: HTMLElement | null = null;
  previewIconElement: HTMLElement | null = null;
  private hoverHighlightElement: HTMLElement | null = null;
  currentEditingTooltip: TooltipConfig | null = null;
  hoveredElement: HTMLElement | null = null;
  pickWarning: string | null = null;
  pickInfo: string | null = null;
  alignmentOptions: { value: TooltipAlignment; label: string }[] = [];
  tooltipAlignmentOptions: { value: TooltipAlignment; label: string }[] = [];
  private pendingSwitchTooltip: TooltipConfig | null = null;

  readonly positionOptions: SimpleSelectOption[] = [
    { label: 'Top', value: TooltipPosition.Top },
    { label: 'Bottom', value: TooltipPosition.Bottom },
    { label: 'Left', value: TooltipPosition.Left },
    { label: 'Right', value: TooltipPosition.Right },
  ];

  private visibleTextLengthCache: { html: string; length: number } | null = null;
  private smartPositionedTooltipId: string | null = null;
  private tooltipPositionOptionsCache: { key: string; options: SimpleSelectOption[] } | null = null;

  // Same as positionOptions, but the selected option is suffixed with
  // "(smart)" when the tooltip is (or would be, once opened) flipped to the
  // opposite side because the configured position doesn't fit the viewport. Cached so simple-select
  // receives a stable array reference between change-detection runs.
  get tooltipPositionOptions(): SimpleSelectOption[] {
    const smartValue = this.currentEditingTooltip && this.smartPositionedTooltipId === this.currentEditingTooltip.id
      ? Number(this.currentEditingTooltip.position)
      : null;
    const key = String(smartValue);
    if (this.tooltipPositionOptionsCache?.key !== key) {
      this.tooltipPositionOptionsCache = {
        key,
        options: this.positionOptions.map(opt =>
          opt.value === smartValue ? { ...opt, label: `${opt.label} (smart)` } : opt)
      };
    }
    return this.tooltipPositionOptionsCache.options;
  }

  readonly iconModeOptions: SimpleSelectOption[] = [
    { label: 'Positioned (floats over page)', value: TooltipIconMode.Positioned },
    { label: 'Inline (next to element)', value: TooltipIconMode.Inline },
  ];

  readonly iconLabelOptions: SimpleSelectOption[] = [
    { label: '? (Question)', value: '?' },
    { label: 'I (Info)', value: 'I' },
  ];

  readonly tooltipAlignTargetOptions: SimpleSelectOption[] = [
    { label: 'Align to container', value: TooltipAlignTarget.Container },
    { label: 'Align to icon', value: TooltipAlignTarget.Icon },
  ];

  readonly sizeOptions: SimpleSelectOption[] = [
    { label: '14px', value: 14 }, { label: '16px', value: 16 }, { label: '18px', value: 18 },
    { label: '20px', value: 20 }, { label: '22px', value: 22 }, { label: '24px', value: 24 },
    { label: '28px', value: 28 }, { label: '32px', value: 32 }, { label: '40px', value: 40 },
  ];

  readonly rotationOptions: SimpleSelectOption[] = [
    { label: '0°', value: 0 }, { label: '45°', value: 45 }, { label: '90°', value: 90 },
    { label: '135°', value: 135 }, { label: '180°', value: 180 }, { label: '225°', value: 225 },
    { label: '270°', value: 270 }, { label: '315°', value: 315 }, { label: '360°', value: 360 },
  ];

  private originalTooltip: TooltipConfig | null = null;
  isPendingNew = false;
  isSaving = false;
  private destroy$ = new Subject<void>();
  private updateTooltip$ = new Subject<void>();
  private applyFilter$ = new Subject<void>();
  private captureListeners: { type: string; handler: EventListener }[] = [];
  private modalRef?: BsModalRef;
  private confirmedNavigationId: number | null = null;
  private modalClose$ = new Subject<boolean>();
  deleteModalMessage = '';
  private pendingDeleteAction: (() => void) | null = null;
  private boundBeforeUnload: (event: Event) => void;

  constructor(
    public tooltipService: TourGuideTooltipService,
    private anchorService: TooltipAnchorService,
    private reanchorService: TooltipReanchorService,
    private angularCtx: AngularContextService,
    private router: Router,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef,
    private modalService: BsModalService,
    private toastr: ToastrService,
    private utils: TooltipUtilsService,
    private zIndex: TooltipZIndexService,
    private tooltipInjector: TooltipInjectorService,
    private unsavedChangesService: TooltipUnsavedChangesService
  ) {
    this.boundBeforeUnload = this.handleBeforeUnload.bind(this);
  }

  ngOnInit(): void {
    document.documentElement.style.setProperty('--tooltip-sidebar-width', '380px');

    this.tooltipService.tooltips$
      .pipe(takeUntil(this.destroy$))
      .subscribe(tooltips => {
        this.tooltips = tooltips;
        this.refreshDomPresence();
        this.performApplyFilter();
      });

    // Icons can also appear later via TooltipReanchorService's background
    // resolution, which tags the DOM directly without emitting on tooltips$ —
    // poll periodically so the "Can't display" status doesn't go stale once
    // that happens.
    interval(1000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.refreshDomPresence());

    // Emits from DOM/resize callbacks that may run outside the Angular zone.
    this.tooltipInjector.smartPositionedTooltipId$
      .pipe(takeUntil(this.destroy$))
      .subscribe(id => this.ngZone.run(() => {
        this.smartPositionedTooltipId = id;
        this.cdr.markForCheck();
      }));

    // Scrolling/resizing moves the target relative to the viewport, which can
    // change whether its tooltip would need to flip.
    this.ngZone.runOutsideAngular(() => {
      merge(fromEvent(window, 'resize'), fromEvent(document, 'scroll', { capture: true }))
        .pipe(debounceTime(150), takeUntil(this.destroy$))
        .subscribe(() => this.checkSmartPosition());
    });

    this.updateTooltip$
      .pipe(debounceTime(500), takeUntil(this.destroy$))
      .subscribe(() => this.performUpdateCurrentTooltip());

    this.applyFilter$
      .pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe(() => this.performApplyFilter());

    window.addEventListener('beforeunload', this.boundBeforeUnload);
    this.unsavedChangesService.register(this.confirmLeavePage);
    this.closeEditorOnPageChange();
  }

  ngOnDestroy(): void {
    window.removeEventListener('beforeunload', this.boundBeforeUnload);
    this.unsavedChangesService.unregister(this.confirmLeavePage);
    document.body.style.cursor = 'default';
    document.documentElement.style.removeProperty('--tooltip-sidebar-width');
    this.stopPicking();
    this.detachEventBlockers();
    this.destroy$.next();
    this.destroy$.complete();
    this.updateTooltip$.complete();
    this.applyFilter$.complete();
    this.modalClose$.complete();
    this.editor.destroy();
  }

  startCreatingNew(): void {
    if (this.currentEditingTooltip && this.hasUnsavedChanges()) {
      this.pendingSwitchTooltip = null;
      this.openConfirmNewModal();
      return;
    }
    this.performCreatingNew();
  }

  private performCreatingNew(): void {
    if (this.currentEditingTooltip) this.clearEditing();
    this.pendingContainerElement = null;
    this.pickingStep = 1;
    this.pickingField = 'container';
    this.isPickingElement = true;
    this.reanchorService.setPickingActive(true);
    this.pickWarning = null;
    this.pickInfo = null;
    document.body.style.cursor = 'crosshair';
    this.attachEventBlockers();
  }

  private openConfirmNewModal(): void {
    this.modalRef = this.modalService.show(this.templateConfirmSwitch, {
      class: 'modal-md modal-dialog-centered',
      ignoreBackdropClick: true,
      keyboard: false,
    });
    const sub = this.modalClose$
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe((confirmed: boolean) => {
        this.modalRef?.hide();
        if (confirmed) {
          this.performCreatingNew();
        }
      });
  }

  startPickingField(field: 'icon' | 'container'): void {
    this.pickingField = field;
    this.isPickingElement = true;
    this.reanchorService.setPickingActive(true);
    this.pickWarning = null;
    this.pickInfo = null;
    document.body.style.cursor = 'crosshair';
    this.attachEventBlockers();
  }

  stopPicking(): void {
    this.isPickingElement = false;
    this.reanchorService.setPickingActive(false);
    this.pickingField = null;
    this.pickingStep = null;
    this.pendingContainerElement = null;
    this.pickWarning = null;
    this.pickInfo = null;
    document.body.style.cursor = 'default';
    this.detachEventBlockers();
    if (this.hoveredElement) {
      this.hoveredElement.classList.remove('tooltip-element-hover');
      this.hoveredElement = null;
    }
    this.removeHoverHighlight();
    this.removePreviewIcon();
  }

  private attachEventBlockers(): void {
    if (this.captureListeners.length > 0) return;

    try {
      const escHandler: EventListener = (event: Event) => {
        if ((event as KeyboardEvent).key === 'Escape') {
          this.ngZone.run(() => this.stopPicking());
        }
      };
      document.addEventListener('keydown', escHandler, true);
      this.captureListeners.push({ type: 'keydown', handler: escHandler });

      // position: fixed highlight doesn't move with the page on its own, so
      // keep it tracking the hovered element if the page scrolls without the
      // mouse moving (e.g. via scroll wheel/keyboard).
      const scrollHandler: EventListener = () => {
        if (this.hoveredElement) {
          this.showHoverHighlight(this.hoveredElement);
        }
      };
      document.addEventListener('scroll', scrollHandler, true);
      this.captureListeners.push({ type: 'scroll', handler: scrollHandler });

      const types = ['click', 'mousedown', 'mouseup', 'pointerdown', 'pointerup', 'touchstart'];
      types.forEach(type => {
        const handler: EventListener = (event: Event) => {
          if (!this.isPickingElement) return;
          const target = event.target as HTMLElement;
          if (this.isToolbarElement(target) || this.isModalElement(target)) return;
          event.stopPropagation();
          if ('stopImmediatePropagation' in event && typeof event.stopImmediatePropagation === 'function') {
            event.stopImmediatePropagation();
          }
          if (event.type === 'click') {
            event.preventDefault();
            this.ngZone.run(() => this.handlePickingClick(event as MouseEvent));
          }
        };
        document.addEventListener(type, handler, true);
        this.captureListeners.push({ type, handler });
      });
    } catch (e) {
      console.error('Failed to attach event blockers:', e);
      this.detachEventBlockers();
      throw e;
    }
  }

  private detachEventBlockers(): void {
    this.captureListeners.forEach(listener => {
      document.removeEventListener(listener.type, listener.handler, true);
    });
    this.captureListeners = [];
  }

  @HostListener('document:mouseover', ['$event'])
  onMouseOver(event: MouseEvent): void {
    if (!this.isPickingElement) return;
    const rawTarget = event.target as HTMLElement;
    if (this.isToolbarElement(rawTarget)) return;
    if (this.isAgGridElement(rawTarget) || this.isDeniedElement(rawTarget)) return;
    const target = rawTarget.closest<HTMLElement>('[data-tooltip-anchor-boundary]') ?? rawTarget;

    if (this.hoveredElement && this.hoveredElement !== target) {
      this.hoveredElement.classList.remove('tooltip-element-hover');
    }
    target.classList.add('tooltip-element-hover');
    this.hoveredElement = target;
    this.showHoverHighlight(target);

    if (this.pickingField === 'icon') {
      this.showPreviewIcon(target);
    } else if (this.pickingStep === 2) {
      this.showPreviewIcon(target);
    }
  }

  @HostListener('document:mouseout', ['$event'])
  onMouseOut(event: MouseEvent): void {
    if (!this.isPickingElement) return;
    const rawTarget = event.target as HTMLElement;
    if (this.isToolbarElement(rawTarget)) return;
    if (this.isAgGridElement(rawTarget) || this.isDeniedElement(rawTarget)) return;
    const target = rawTarget.closest<HTMLElement>('[data-tooltip-anchor-boundary]') ?? rawTarget;

    if (this.hoveredElement === target) {
      target.classList.remove('tooltip-element-hover');
      this.hoveredElement = null;
      this.removeHoverHighlight();
    }
    if (this.pickingField === 'icon' || this.pickingStep === 2) {
      this.removePreviewIcon();
    }
  }

  private showHoverHighlight(target: HTMLElement): void {
    if (!this.hoverHighlightElement) {
      this.hoverHighlightElement = document.createElement('div');
      this.hoverHighlightElement.className = 'tooltip-element-hover-outline';
      this.hoverHighlightElement.style.zIndex = String(this.zIndex.HIGHLIGHTED_ELEMENT);
      document.body.appendChild(this.hoverHighlightElement);
    }
    // position: fixed uses the same coordinate system getBoundingClientRect()
    // returns, so the rect can be applied directly with no scroll-offset math
    // (that math was the one hand-computed part of this file, and the likely
    // source of the reported left-shift on picking mode's hover highlight).
    const rect = target.getBoundingClientRect();
    this.hoverHighlightElement.style.top = `${rect.top}px`;
    this.hoverHighlightElement.style.left = `${rect.left}px`;
    this.hoverHighlightElement.style.width = `${rect.width}px`;
    this.hoverHighlightElement.style.height = `${rect.height}px`;
  }

  private removeHoverHighlight(): void {
    if (this.hoverHighlightElement) {
      this.hoverHighlightElement.remove();
      this.hoverHighlightElement = null;
    }
  }

  private handlePickingClick(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    const rawTarget = event.target as HTMLElement;

    if (this.isToolbarElement(rawTarget) || this.isModalElement(rawTarget)) return;

    if (this.hoveredElement) {
      this.hoveredElement.classList.remove('tooltip-element-hover');
      this.hoveredElement = null;
    }
    this.removeHoverHighlight();

    if (this.isAgGridElement(rawTarget) || this.isDeniedElement(rawTarget)) {
      this.pickWarning = 'This element is inside a data grid or repeated list and cannot be used as a tooltip anchor. Select a stable element outside it instead.';
      this.toastr.error('Tooltip cannot be added to dynamic content');
      return;
    }

    // Redirect to the stable anchor-boundary ancestor, if any, so the anchor
    // survives even when the exact clicked element only exists for one
    // permission state (e.g. an editable control vs. its read-only display).
    const boundary = rawTarget.closest<HTMLElement>('[data-tooltip-anchor-boundary]');
    const target = boundary ?? rawTarget;
    const wasRedirected = boundary !== null && boundary !== rawTarget;

    const classification = this.angularCtx.classifyElement(target);
    this.pickWarning = null;
    this.pickInfo = null;

    if (classification === 'ngFor') {
      this.pickWarning = 'This element is part of a repeated list. Select the list container instead.';
      this.toastr.error('Tooltip cannot be added to dynamic content');
      return;
    }
    if (classification === 'ngIf') {
      this.pickInfo = 'This element appears conditionally. The tooltip will hide when the element is not rendered.';
    }
    if (classification === 'unknown') {
      this.pickInfo = 'Element stability could not be verified.';
    }
    if (wasRedirected) {
      this.pickInfo = 'This element is inside a permission-dependent form. The tooltip will attach to its stable container instead.';
    }

    if (this.pickingStep === 1) {
      // 2-step flow: step 1 done, move to step 2
      this.pendingContainerElement = target;
      this.pickingStep = 2;
      this.pickingField = 'icon';
      this.pickWarning = null;
      this.pickInfo = null;
      return;
    }

    this.isPickingElement = false;
    document.body.style.cursor = 'default';
    this.detachEventBlockers();
    this.removePreviewIcon();

    if (this.pickingStep === 2) {
      // 2-step flow: step 2 done, create tooltip
      this.createFromTwoStepSelection(this.pendingContainerElement!, target);
      this.pendingContainerElement = null;
      this.pickingStep = null;
      this.pickingField = null;
    } else if (this.pickingField === 'container') {
      this.saveContainerFromSelection(target);
      this.pickingField = null;
    } else {
      this.saveIconFromSelection(target);
      this.pickingField = null;
    }
    // Resync after the pick completes, not before, so a brand-new tooltip's
    // component tag is included in the freshly-resynced observer set.
    this.reanchorService.setPickingActive(false);
  }

  private isToolbarElement(element: HTMLElement): boolean {
    return element.closest('app-tooltip-admin-toolbar') !== null ||
           element.closest('.picking-mode-indicator') !== null;
  }

  private isModalElement(element: HTMLElement): boolean {
    return element.closest('.modal') !== null ||
           element.closest('.modal-backdrop') !== null;
  }

  // ag-Grid renders its rows/cells outside Angular's view tree (its own internal
  // engine, not *ngFor), so classifyElement can never see them as repeated
  // content — every ag-Grid instance mounts as a child of <ag-grid-angular>,
  // which is a reliable, version-independent selector to exclude the whole grid.
  private isAgGridElement(element: HTMLElement): boolean {
    return element.closest('ag-grid-angular') !== null;
  }

  // Explicit opt-out for *ngFor/*cdkVirtualFor content, via a static
  // data-tooltip-no-anchor attribute (no directive needed, so it works in
  // every module without import wiring). classifyElement's *ngFor detection
  // reverse-engineers private Ivy internals and can fail open (e.g. it missed
  // a kanban card in upload-list), so this gives template authors a reliable,
  // Angular-version-independent fallback.
  private isDeniedElement(element: HTMLElement): boolean {
    return element.closest('[data-tooltip-no-anchor]') !== null;
  }

  // Matches TooltipInjectorService.isOverlayTarget: a block-level element that
  // holds other boxes (card / wrapper / button row) — not a text element like a
  // heading or paragraph, which keeps the in-flow icon.
  private isOverlayEl(el: HTMLElement): boolean {
    const d = window.getComputedStyle(el).display;
    const blockLevel = d === 'block' || d === 'flex' || d === 'grid'
      || d === 'table' || d === 'table-cell' || d === 'list-item'
      || d === 'table-row' || d === 'table-caption';
    if (!blockLevel) return false;
    return Array.from(el.children).some(child => {
      if (child.classList.contains('tour-guide-question-icon')) return false;
      const cd = window.getComputedStyle(child as HTMLElement).display;
      return cd !== 'inline' && cd !== 'contents' && cd !== 'none';
    });
  }

  private showPreviewIcon(target: HTMLElement): void {
    if (this.isToolbarElement(target)) return;
    this.removePreviewIcon();

    this.previewIconElement = document.createElement('span');
    this.previewIconElement.className = 'tour-guide-question-icon-preview';
    this.previewIconElement.textContent = this.currentEditingTooltip?.iconLabel ?? '?';
    this.previewIconElement.style.pointerEvents = 'none';
    this.previewIconElement.style.zIndex = '10';

    // Mirror exactly how the injector places an inline icon
    if (this.isOverlayEl(target)) {
      // Layout container: the injector overlays the icon (absolute) so it
      // doesn't reflow the container's content — preview it the same way. Small
      // wrappers (no room inside) get the icon just outside the right edge.
      if (window.getComputedStyle(target).position === 'static') {
        target.style.position = 'relative';
        target.dataset['tourGuidePreviewRelative'] = '1';
      }
      const rect = target.getBoundingClientRect();
      const outside = rect.height < 44 || rect.width < 55;
      this.previewIconElement.style.position = 'absolute';
      this.previewIconElement.style.margin = '0';
      if (outside) {
        this.previewIconElement.style.top = 'calc(50% - 11px)';
        this.previewIconElement.style.left = 'calc(100% + 6px)';
      } else {
        this.previewIconElement.style.top = '10px';
        this.previewIconElement.style.right = '10px';
      }
      target.appendChild(this.previewIconElement);
    } else if (this.isBlockLevelEl(target)) {
      // Block text element (h2 / p): icon trails the text, inside the element.
      target.appendChild(this.previewIconElement);
    } else {
      const parent = target.parentElement;
      if (parent) {
        const next = target.nextSibling;
        if (next) parent.insertBefore(this.previewIconElement, next);
        else parent.appendChild(this.previewIconElement);
      } else {
        target.appendChild(this.previewIconElement);
      }
    }
  }

  private isBlockLevelEl(el: HTMLElement): boolean {
    const d = window.getComputedStyle(el).display;
    return d === 'block' || d === 'flex' || d === 'grid'
      || d === 'table' || d === 'table-cell' || d === 'list-item'
      || d === 'table-row' || d === 'table-caption';
  }

  private removePreviewIcon(): void {
    if (this.previewIconElement && this.previewIconElement.parentNode) {
      const host = this.previewIconElement.parentElement;
      if (host?.dataset['tourGuidePreviewRelative']) {
        host.style.removeProperty('position');
        delete host.dataset['tourGuidePreviewRelative'];
      }
      this.previewIconElement.parentNode.removeChild(this.previewIconElement);
      this.previewIconElement = null;
    }
  }

  private saveIconFromSelection(element: HTMLElement): void {
    if (!this.currentEditingTooltip) return;
    const anchor = this.anchorService.captureAnchor(element);
    const stability = this.anchorService.assignStability(anchor);
    this.currentEditingTooltip = { ...this.currentEditingTooltip, anchor, anchorStability: stability };
    element.setAttribute('data-tooltip-id', this.currentEditingTooltip.id);
    // Local only — persisted when the admin clicks "Save tooltip", same as
    // every other field. Re-picking must not fire a network request by itself.
    this.cdr.detectChanges();
  }

  private saveContainerFromSelection(element: HTMLElement): void {
    if (!this.currentEditingTooltip) return;
    const hoverAnchor = this.anchorService.captureAnchor(element);
    this.currentEditingTooltip = { ...this.currentEditingTooltip, hoverAnchor };
    // Local only — persisted when the admin clicks "Save tooltip".
    this.cdr.detectChanges();
  }

  private createFromTwoStepSelection(containerElement: HTMLElement, iconElement: HTMLElement): void {
    const hoverAnchor = this.anchorService.captureAnchor(containerElement);
    const anchor = this.anchorService.captureAnchor(iconElement);
    const stability = this.anchorService.assignStability(anchor);

    // Generate ID and mark both elements BEFORE adding to service to prevent race condition with injector
    const tempId = `pending:${crypto.randomUUID()}`;
    iconElement.setAttribute('data-tooltip-id', tempId);
    containerElement.setAttribute('data-tooltip-hover-id', tempId);

    const pending = this.tooltipService.addTooltipPending({
      anchor,
      hoverAnchor,
      anchorStability: stability,
      header: 'Help',
      text: 'Enter tooltip text here',
      position: TooltipPosition.Bottom,
      tooltipAlign: TooltipAlignment.Center,
      tooltipAlignTarget: TooltipAlignTarget.Container,
      iconPlacement: TooltipPosition.Right,
      iconPlacementMode: TooltipIconMode.Inline,
      // Block-level targets get an overlaid icon pinned to a corner — Top/Right
      // reads best there. Inline targets keep the in-flow centered default.
      iconAlign: this.isOverlayEl(iconElement) ? TooltipAlignment.Top : TooltipAlignment.Center,
      iconRotation: 0,
      iconOffsetTop: 0,
      iconOffsetLeft: 0,
      iconSize: 22,
      iconLabel: '?',
      showContainerBorder: true,
      containerBorderWidth: 1,
      containerBorderColor: '#9CA3AF',
      hideIconAfterOpen: false,
      isEnabled: true
    }, tempId);

    this.isPendingNew = true;
    this.originalTooltip = null;
    // Clone rather than alias `pending` directly — it's the same object the
    // injector just stored as its tracked instance's config, so mutating it
    // in place via ngModel (instead of going through a fresh previewTooltip()
    // object) would update the injector's "old" snapshot before the diff
    // that decides whether the DOM needs to change ever runs, silently
    // swallowing the very first live edit.
    this.currentEditingTooltip = { ...pending };
    this.updateAlignmentOptions();
    this.updateTooltipAlignmentOptions();
    this.cdr.detectChanges();
    this.scheduleSmartPositionCheck();
  }

  editTooltip(tooltip: TooltipConfig): void {
    if (this.currentEditingTooltip && this.hasUnsavedChanges()) {
      this.pendingSwitchTooltip = tooltip;
      this.openConfirmSwitchModal();
      return;
    }
    this.performEditTooltip(tooltip);
  }

  private performEditTooltip(tooltip: TooltipConfig): void {
    // Discard any pending-new or unsaved preview from the previously edited tooltip
    if (this.currentEditingTooltip) {
      if (this.isPendingNew) {
        const tempId = this.currentEditingTooltip.id;
        document.querySelector(`[data-tooltip-id="${tempId}"]`)?.removeAttribute('data-tooltip-id');
        this.tooltipService.removePendingTooltip(tempId);
      } else if (this.originalTooltip) {
        this.tooltipService.previewTooltip(this.currentEditingTooltip.id, this.originalTooltip);
      }
    }
    this.isPendingNew = false;
    this.originalTooltip = { ...tooltip };
    this.currentEditingTooltip = {
      ...tooltip,
      iconPlacement: tooltip.iconPlacement ?? TooltipPosition.Right,
      iconAlign: tooltip.iconAlign ?? TooltipAlignment.Center,
      iconRotation: tooltip.iconRotation ?? 0,
      tooltipAlign: tooltip.tooltipAlign ?? TooltipAlignment.Center,
      tooltipAlignTarget: tooltip.tooltipAlignTarget ?? TooltipAlignTarget.Container,
      iconSize: tooltip.iconSize ?? 22,
      iconLabel: tooltip.iconLabel ?? '?',
      showContainerBorder: tooltip.showContainerBorder ?? true,
      containerBorderWidth: tooltip.containerBorderWidth ?? 1,
      containerBorderColor: tooltip.containerBorderColor ?? '#9CA3AF',
      hideIconAfterOpen: tooltip.hideIconAfterOpen ?? false
    };
    this.updateAlignmentOptions();
    this.updateTooltipAlignmentOptions();
    this.scheduleSmartPositionCheck();
  }

  // Deferred a tick so the preview edit (and any freshly injected icon) has
  // landed in the DOM before the bubble is measured.
  private scheduleSmartPositionCheck(): void {
    setTimeout(() => this.checkSmartPosition());
  }

  private checkSmartPosition(): void {
    if (this.currentEditingTooltip) this.tooltipInjector.checkSmartPosition(this.currentEditingTooltip);
  }

  onTooltipPositionChange(): void {
    this.updateTooltipAlignmentOptions();
  }

  onPlacementChange(): void {
    this.updateAlignmentOptions();
  }

  // "Hide icon after opening" only applies when aligning to the container —
  // the toggle is hidden for icon alignment, so reset it rather than keep a
  // stale true that would still hide the icon the bubble is anchored to.
  onTooltipAlignTargetChange(): void {
    if (this.currentEditingTooltip?.tooltipAlignTarget === TooltipAlignTarget.Icon) {
      this.currentEditingTooltip.hideIconAfterOpen = false;
    }
    this.updateCurrentTooltipImmediate();
  }

  // CSS `top` moves an element away from the top edge (positive = down), which
  // reads backwards next to a field literally named "Offset Top" — admins expect
  // a positive number to move the icon up. Only the form input is flipped here;
  // the stored value and every rendering path (live preview, the actual tooltip)
  // keep using the real CSS-matching sign, so existing tooltips don't shift.
  get iconOffsetTopDisplay(): number {
    return -(this.currentEditingTooltip?.iconOffsetTop ?? 0);
  }

  set iconOffsetTopDisplay(value: number) {
    if (!this.currentEditingTooltip) return;
    this.currentEditingTooltip.iconOffsetTop = -(value ?? 0);
  }

  // Same for "Offset Left": CSS `left` moves right for a positive value, admins
  // expect a positive number to move the icon left.
  get iconOffsetLeftDisplay(): number {
    return -(this.currentEditingTooltip?.iconOffsetLeft ?? 0);
  }

  set iconOffsetLeftDisplay(value: number) {
    if (!this.currentEditingTooltip) return;
    this.currentEditingTooltip.iconOffsetLeft = -(value ?? 0);
  }

  updateAlignmentOptions(): void {
    if (!this.currentEditingTooltip) { this.alignmentOptions = []; return; }
    const placement = this.currentEditingTooltip.iconPlacement;
    if (placement === TooltipPosition.Top || placement === TooltipPosition.Bottom) {
      this.alignmentOptions = [
        { value: TooltipAlignment.Left, label: 'Left' },
        { value: TooltipAlignment.Center, label: 'Center' },
        { value: TooltipAlignment.Right, label: 'Right' }
      ];
      const validH = [TooltipAlignment.Left, TooltipAlignment.Center, TooltipAlignment.Right];
      if (this.currentEditingTooltip.iconAlign === undefined || !validH.includes(this.currentEditingTooltip.iconAlign)) {
        this.currentEditingTooltip.iconAlign = TooltipAlignment.Center;
      }
    } else {
      this.alignmentOptions = [
        { value: TooltipAlignment.Top, label: 'Top' },
        { value: TooltipAlignment.Center, label: 'Center' },
        { value: TooltipAlignment.Bottom, label: 'Bottom' }
      ];
      const validV = [TooltipAlignment.Top, TooltipAlignment.Center, TooltipAlignment.Bottom];
      if (this.currentEditingTooltip.iconAlign === undefined || !validV.includes(this.currentEditingTooltip.iconAlign)) {
        this.currentEditingTooltip.iconAlign = TooltipAlignment.Center;
      }
    }
  }

  updateTooltipAlignmentOptions(): void {
    if (!this.currentEditingTooltip) { this.tooltipAlignmentOptions = []; return; }
    const position = this.currentEditingTooltip.position;
    if (position === TooltipPosition.Top || position === TooltipPosition.Bottom) {
      this.tooltipAlignmentOptions = [
        { value: TooltipAlignment.Left, label: 'Left' },
        { value: TooltipAlignment.Center, label: 'Center' },
        { value: TooltipAlignment.Right, label: 'Right' }
      ];
      const validH = [TooltipAlignment.Left, TooltipAlignment.Center, TooltipAlignment.Right];
      if (this.currentEditingTooltip.tooltipAlign === undefined || !validH.includes(this.currentEditingTooltip.tooltipAlign)) {
        this.currentEditingTooltip.tooltipAlign = TooltipAlignment.Center;
      }
    } else {
      this.tooltipAlignmentOptions = [
        { value: TooltipAlignment.Top, label: 'Top' },
        { value: TooltipAlignment.Center, label: 'Center' },
        { value: TooltipAlignment.Bottom, label: 'Bottom' }
      ];
      const validV = [TooltipAlignment.Top, TooltipAlignment.Center, TooltipAlignment.Bottom];
      if (this.currentEditingTooltip.tooltipAlign === undefined || !validV.includes(this.currentEditingTooltip.tooltipAlign)) {
        this.currentEditingTooltip.tooltipAlign = TooltipAlignment.Center;
      }
    }
  }

  updateCurrentTooltip(): void {
    this.updateTooltip$.next();
  }

  // The input's min/max attributes only limit the spinner — typed values bypass them.
  // Writing the clamped value back to the DOM is needed because ngModel won't
  // re-render when the model ends up equal to its previous value.
  onBorderWidthChange(input: HTMLInputElement): void {
    if (!this.currentEditingTooltip) return;
    const width = this.currentEditingTooltip.containerBorderWidth;
    // Emptied mid-typing: keep the field empty and don't preview an invalid width.
    // onBorderWidthBlur restores the minimum.
    if (width === undefined || !Number.isFinite(width)) return;
    // Rounded because the backend's border width is an int: 2.5 would fail the save.
    this.setBorderWidth(input, Math.round(Math.min(Math.max(width, BORDER_WIDTH_MIN), BORDER_WIDTH_MAX)));
    this.updateCurrentTooltip();
  }

  // An emptied field stays empty while typing, so a new value can be entered;
  // it falls back to the minimum once the field loses focus.
  onBorderWidthBlur(input: HTMLInputElement): void {
    if (!this.currentEditingTooltip) return;
    if (!Number.isFinite(this.currentEditingTooltip.containerBorderWidth)) {
      this.setBorderWidth(input, BORDER_WIDTH_MIN);
      this.updateCurrentTooltip();
    }
  }

  private setBorderWidth(input: HTMLInputElement, width: number): void {
    if (!this.currentEditingTooltip || this.currentEditingTooltip.containerBorderWidth === width) return;
    this.currentEditingTooltip.containerBorderWidth = width;
    input.value = String(width);
  }

  // For discrete controls (dropdowns, toggles) — these fire once per click, so
  // debouncing them alongside the free-text header/text fields just means a
  // quick run of selections silently drops every pick but the last.
  updateCurrentTooltipImmediate(): void {
    this.performUpdateCurrentTooltip();
  }

  private performUpdateCurrentTooltip(): void {
    if (!this.currentEditingTooltip) return;
    this.tooltipService.previewTooltip(this.currentEditingTooltip.id, {
      header: this.currentEditingTooltip.header,
      text: this.currentEditingTooltip.text,
      position: this.currentEditingTooltip.position,
      tooltipAlign: this.currentEditingTooltip.tooltipAlign,
      tooltipAlignTarget: this.currentEditingTooltip.tooltipAlignTarget,
      iconPlacement: this.currentEditingTooltip.iconPlacement,
      iconPlacementMode: this.currentEditingTooltip.iconPlacementMode,
      iconAlign: this.currentEditingTooltip.iconAlign,
      iconRotation: this.currentEditingTooltip.iconRotation,
      iconOffsetTop: this.currentEditingTooltip.iconOffsetTop,
      iconOffsetLeft: this.currentEditingTooltip.iconOffsetLeft,
      iconSize: this.currentEditingTooltip.iconSize,
      iconLabel: this.currentEditingTooltip.iconLabel,
      showContainerBorder: this.currentEditingTooltip.showContainerBorder,
      containerBorderWidth: this.currentEditingTooltip.containerBorderWidth,
      containerBorderColor: this.currentEditingTooltip.containerBorderColor,
      hideIconAfterOpen: this.currentEditingTooltip.hideIconAfterOpen,
      isEnabled: this.currentEditingTooltip.isEnabled
    });
    this.scheduleSmartPositionCheck();
  }

  saveChanges(): void {
    if (!this.currentEditingTooltip || this.isSaving) return;
    const editing = this.currentEditingTooltip;
    this.isSaving = true;
    if (this.isPendingNew) {
      // POST for the first time — replace temp entry with server-returned real ID
      const tempId = this.currentEditingTooltip.id;
      const snapshot = { ...this.currentEditingTooltip, hideIconAfterOpen: this.savedHideIconAfterOpen() };
      this.tooltipService.persistPendingTooltip(tempId, snapshot)
        .pipe(finalize(() => this.isSaving = false), takeUntil(this.destroy$))
        .subscribe(saved => {
          document.querySelectorAll(`[data-tooltip-id="${tempId}"]`).forEach(el => el.setAttribute('data-tooltip-id', saved.id));
          document.querySelectorAll(`[data-tooltip-hover-id="${tempId}"]`).forEach(el => el.setAttribute('data-tooltip-hover-id', saved.id));
          this.tooltipInjector.renameInstanceId(tempId, saved.id);
          this.tooltipService.replacePendingWithSaved(tempId, saved);
          this.finishEditing(editing);
        });
    } else {
      // PUT for existing tooltip
      this.tooltipService.saveTooltip(this.currentEditingTooltip.id, {
        anchor: this.currentEditingTooltip.anchor,
        hoverAnchor: this.currentEditingTooltip.hoverAnchor,
        anchorStability: this.currentEditingTooltip.anchorStability,
        header: this.currentEditingTooltip.header,
        text: this.currentEditingTooltip.text,
        position: this.currentEditingTooltip.position,
        tooltipAlign: this.currentEditingTooltip.tooltipAlign,
        tooltipAlignTarget: this.currentEditingTooltip.tooltipAlignTarget,
        iconPlacement: this.currentEditingTooltip.iconPlacement,
        iconPlacementMode: this.currentEditingTooltip.iconPlacementMode,
        iconAlign: this.currentEditingTooltip.iconAlign,
        iconRotation: this.currentEditingTooltip.iconRotation,
        iconOffsetTop: this.currentEditingTooltip.iconOffsetTop,
        iconOffsetLeft: this.currentEditingTooltip.iconOffsetLeft,
        iconSize: this.currentEditingTooltip.iconSize,
        iconLabel: this.currentEditingTooltip.iconLabel,
        showContainerBorder: this.currentEditingTooltip.showContainerBorder,
        containerBorderWidth: this.currentEditingTooltip.containerBorderWidth,
        containerBorderColor: this.currentEditingTooltip.containerBorderColor,
        hideIconAfterOpen: this.savedHideIconAfterOpen(),
        isEnabled: this.currentEditingTooltip.isEnabled
      })
        .pipe(finalize(() => this.isSaving = false), takeUntil(this.destroy$))
        .subscribe(() => this.finishEditing(editing));
    }
  }

  // Tooltips saved before the toggle was hidden for icon alignment can still
  // carry hideIconAfterOpen = true; never write that combination again.
  private savedHideIconAfterOpen(): boolean {
    return this.currentEditingTooltip?.tooltipAlignTarget === TooltipAlignTarget.Icon
      ? false
      : !!this.currentEditingTooltip?.hideIconAfterOpen;
  }

  // Closes the editor only after the server accepted the save; on failure the
  // service shows a toastr and the panel keeps the admin's unsaved input.
  private finishEditing(saved: TooltipConfig): void {
    if (this.currentEditingTooltip !== saved) return;
    this.isPendingNew = false;
    this.originalTooltip = null;
    this.currentEditingTooltip = null;
  }

  clearEditing(): void {
    if (this.currentEditingTooltip) {
      if (this.isPendingNew) {
        // Remove the unsaved tooltip from local state entirely
        const tempId = this.currentEditingTooltip.id;
        document.querySelector(`[data-tooltip-id="${tempId}"]`)?.removeAttribute('data-tooltip-id');
        this.tooltipService.removePendingTooltip(tempId);
      } else if (this.originalTooltip) {
        // Revert field-edit preview to last-saved state
        this.tooltipService.previewTooltip(this.currentEditingTooltip.id, this.originalTooltip);
      }
    }
    this.isPendingNew = false;
    this.originalTooltip = null;
    this.currentEditingTooltip = null;
  }

  deleteTooltipsOnCurrentRoute(): void {
    const route = this.normalizeRoute(this.router.url.split('?')[0]);
    this.openDeleteModal(`Delete all tooltips on this page?`, () => {
      this.tooltipService.deleteByRoute(route);
      this.toastr.success('Tooltips deleted successfully');
    });
  }

  private normalizeRoute(url: string): string {
    return this.utils.normalizeRoute(url);
  }

  deleteTooltip(id: string): void {
    this.openDeleteModal('Delete this tooltip?', () => {
      this.tooltipService.deleteTooltip(id);
      this.toastr.success('Tooltip deleted successfully');
    });
  }

  exportTooltips(): void {
    this.performExport('all');
  }

  exportCurrentPageTooltips(): void {
    this.performExport('current');
  }

  private performExport(scope: 'all' | 'current'): void {
    const currentRoute = this.normalizeRoute(this.router.url.split('?')[0]);
    const params: { scope: 'all' | 'current'; route?: string } = { scope };

    if (scope === 'current') {
      params.route = currentRoute;
    }

    this.tooltipService.exportCsv(params).subscribe({
      next: (blob: Blob) => {
        try {
          const link = document.createElement('a');
          const url = URL.createObjectURL(blob);
          link.setAttribute('href', url);
          link.setAttribute('download', `tooltips_export_${new Date().toISOString().split('T')[0]}.csv`);
          link.style.visibility = 'hidden';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          this.toastr.success('Tooltip configurations exported successfully');
        } catch (e) {
          console.error('Export download failed:', e);
          this.toastr.error('Failed to download file. Please try again.');
        }
      },
    });
  }

  importTooltips(event: Event, scope: 'all' | 'current' = 'all'): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const csvContent = e.target?.result as string;
        this.openImportConfirmation(csvContent, scope);
      } catch (err) {
        console.error('Import error:', err);
        this.toastr.error('Failed to read CSV file');
      }
      input.value = '';
    };
    reader.readAsText(file);
  }

  private openImportConfirmation(csvContent: string, scope: 'all' | 'current'): void {
    this.deleteModalMessage = 'Existing tooltips and their configurations may be updated or overwritten. Do you want to continue?';
    this.modalRef = this.modalService.show(this.templateDeleteConfirm, {
      class: 'modal-md modal-dialog-centered',
      ignoreBackdropClick: true,
      keyboard: false,
    });
    const sub = this.modalClose$
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe((confirmed: boolean) => {
        this.modalRef?.hide();
        if (confirmed) {
          this.performImport(csvContent, scope);
        }
      });
  }

  private performImport(csvContent: string, scope: 'all' | 'current' = 'all'): void {
    const currentRoute = this.normalizeRoute(this.router.url.split('?')[0]);

    this.tooltipService.importCsv(csvContent, scope, currentRoute).subscribe({
      next: (result: { created: number; updated: number; skipped: number; invalid: number; errors: any[] }) => {
        if (result.created === 0 && result.updated === 0 && result.invalid === 0) {
          this.toastr.error('Import failed. Please check the CSV file format and required fields.');
        } else if (result.invalid > 0 || result.skipped > 0) {
          this.toastr.warning('Tooltip configurations were partially imported. Some rows were skipped or invalid.');
        } else {
          this.toastr.success('Tooltip configurations imported successfully');
        }
      },
    });
  }

  clearAllTooltips(): void {
    this.openDeleteModal('Delete all tooltips?', () => {
      this.tooltipService.clearAllTooltips();
      this.toastr.success('All tooltips deleted successfully');
    });
  }

  private openDeleteModal(message: string, action: () => void): void {
    this.deleteModalMessage = message;
    this.pendingDeleteAction = action;
    this.modalRef = this.modalService.show(this.templateDeleteConfirm, {
      class: 'modal-md modal-dialog-centered',
      ignoreBackdropClick: true,
      keyboard: false,
    });
    const sub = this.modalClose$
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe((confirmed: boolean) => {
        this.modalRef?.hide();
        if (confirmed && this.pendingDeleteAction) this.pendingDeleteAction();
        this.pendingDeleteAction = null;
      });
  }

  onDeleteConfirm(): void { this.modalClose$.next(true); }
  onDeleteCancel(): void  { this.modalClose$.next(false); }

  trackById(_: number, tooltip: TooltipConfig): string { return tooltip.id; }

isTooltipInDom(id: string): boolean {
    return this.domPresenceIds.has(id);
  }

  private refreshDomPresence(): void {
    this.domPresenceIds = new Set(
      this.tooltips
        .map(t => t.id)
        .filter(id => !!document.querySelector(`[data-tooltip-id="${id}"]`))
    );
  }

  stabilityLabel(stability?: AnchorStability): string {
    switch (stability) {
      case AnchorStability.Pinned:  return '🟢';
      case AnchorStability.High:    return '🔵';
      case AnchorStability.Medium:  return '🟡';
      default:                      return '🔴';
    }
  }

  getTooltipInfo(tooltip: TooltipConfig): string {
    return `${tooltip.anchor?.componentTag ?? '?'} / ${tooltip.anchor?.route ?? '?'}`;
  }

  getAnchorScore(tooltip: TooltipConfig): number {
    if (!tooltip.anchor) return 0;
    return this.anchorService.computeMaxScore(tooltip.anchor);
  }

  applyFilter(): void {
    this.applyFilter$.next();
  }

  private performApplyFilter(): void {
    if (!this.searchQuery.trim()) {
      this.filteredTooltips = [...this.tooltips];
    } else {
      const query = this.searchQuery.toLowerCase();
      // Match the plain text the list shows, not the stored HTML markup.
      this.filteredTooltips = this.tooltips.filter(t =>
        (t.header?.toLowerCase().includes(query) ?? false) ||
        stripHtml(t.text)
          .toLowerCase()
          .includes(query)
      );
    }
  }

  onSearchChange(): void {
    this.applyFilter();
  }

  private hasUnsavedChanges(): boolean {
    if (!this.currentEditingTooltip || !this.originalTooltip) return false;
    // anchor and hoverAnchor are compared by reference: opening a tooltip copies
    // them as they are, and only re-picking the icon or container replaces them.
    const fields = ['anchor', 'hoverAnchor', 'anchorStability',
                    'header', 'text', 'position', 'tooltipAlign', 'tooltipAlignTarget', 'iconPlacement', 'iconPlacementMode',
                    'iconAlign', 'iconRotation', 'iconOffsetTop', 'iconOffsetLeft', 'iconSize', 'iconLabel',
                    'showContainerBorder', 'containerBorderWidth', 'containerBorderColor', 'hideIconAfterOpen', 'isEnabled'] as const;
    return fields.some(f => this.currentEditingTooltip![f] !== this.originalTooltip![f]);
  }

  private handleBeforeUnload(event: Event): void {
    if (this.hasPendingEdits()) {
      const beforeUnloadEvent = event as BeforeUnloadEvent;
      beforeUnloadEvent.preventDefault();
      beforeUnloadEvent.returnValue = '';
    }
  }

  private openConfirmSwitchModal(): void {
    this.modalRef = this.modalService.show(this.templateConfirmSwitch, {
      class: 'modal-md modal-dialog-centered',
      ignoreBackdropClick: true,
      keyboard: false,
    });
    const sub = this.modalClose$
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe((confirmed: boolean) => {
        this.modalRef?.hide();
        if (confirmed && this.pendingSwitchTooltip) {
          this.performEditTooltip(this.pendingSwitchTooltip);
        }
        this.pendingSwitchTooltip = null;
      });
  }

  // An unsaved new tooltip counts too: its anchors point at the page being left.
  private hasPendingEdits(): boolean {
    return !!this.currentEditingTooltip && (this.isPendingNew || this.hasUnsavedChanges());
  }

  // Called by tooltipUnsavedChangesGuard before every page change. The edits are
  // discarded on NavigationEnd by closeEditorOnPageChange(), not here, so a later
  // guard that rejects the navigation leaves them in place. The guard runs once
  // per activated child route; the navigation id keeps it from asking twice.
  private readonly confirmLeavePage = (): Observable<boolean> => {
    const navigationId = this.router.getCurrentNavigation()?.id ?? null;
    if (navigationId !== null && navigationId === this.confirmedNavigationId) return of(true);
    if (!this.hasPendingEdits()) return of(true);

    this.modalRef = this.modalService.show(this.templateConfirmSwitch, {
      class: 'modal-md modal-dialog-centered',
      ignoreBackdropClick: true,
      keyboard: false,
    });
    return this.modalClose$.pipe(
      take(1),
      tap(confirmed => {
        if (confirmed) this.confirmedNavigationId = navigationId;
      }),
      // finalize also runs when a newer navigation cancels this one and
      // unsubscribes, so the dialog never stays open without a reference.
      finalize(() => {
        this.modalRef?.hide();
        this.modalRef = undefined;
      })
    );
  };

  // The edited tooltip belongs to the page it was opened on, so a page change
  // returns the panel to the list. Query-parameter and fragment changes stay on
  // the page.
  private closeEditorOnPageChange(): void {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        map(event => pagePath(event.urlAfterRedirects)),
        startWith(pagePath(this.router.url)),
        distinctUntilChanged(),
        skip(1),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        if (this.isPickingElement) this.stopPicking();
        if (this.currentEditingTooltip) this.clearEditing();
      });
  }

  onConfirmSwitch(): void { this.modalClose$.next(true); }
  onCancelSwitch(): void { this.modalClose$.next(false); }

  // Toggles the list at the current/last selection to the given type — nested
  // lists (Tab to indent inside a list item) can mix bullet and numbered levels.
  setListType(type: 'bullet' | 'ordered'): void {
    const commands = this.editor.commands;
    if (type === 'bullet') {
      commands.toggleBulletList();
    } else {
      commands.toggleOrderedList();
    }
    commands.exec();
    this.editor.view.focus();
  }

  // Only http(s):// and www. links are accepted — this also blocks dangerous
  // schemes (javascript:, data:, etc.) that new URL() alone would happily
  // parse. The browser's own URL parser (not ngx-editor's dotted-domain regex,
  // which rejects "localhost"/bare IPs/ports) then validates the rest.
  private normalizeLinkUrl(href: string): string | null {
    const trimmed = href.trim();
    if (!trimmed) return null;

    const lower = trimmed.toLowerCase();
    const candidate = lower.startsWith('http://') || lower.startsWith('https://')
      ? trimmed
      : lower.startsWith('www.')
        ? `https://${trimmed}`
        : null;
    if (!candidate) return null;

    try {
      return new URL(candidate).toString();
    } catch {
      return null;
    }
  }

  isValidLinkUrl(href: string): boolean {
    return this.normalizeLinkUrl(href) !== null;
  }

  // ngx-editor's own link popup requires display text; this one falls back to
  // the URL itself when left blank, inserting at the current/last selection.
  insertLink(toggleEl: HTMLElement): void {
    const href = this.normalizeLinkUrl(this.linkForm.href);
    if (!href) return;
    const text = this.linkForm.text.trim() || this.linkForm.href.trim();

    // title = href so hovering a link whose display text differs from its
    // URL (e.g. "click here") reveals the actual destination.
    const commands = this.editor.commands;
    commands.insertLink(text, { href, target: '_blank', title: href });
    commands.exec();
    this.editor.view.focus();

    this.linkForm = { href: '', text: '' };
    (window as any).bootstrap?.Dropdown?.getOrCreateInstance(toggleEl)?.hide();
  }

  validateHeaderLength(value: string): boolean {
    return (value?.length ?? 0) <= this.HEADER_MAX_LENGTH;
  }

  validateTextLength(value: string): boolean {
    return this.getVisibleTextLength(value) <= this.TEXT_MAX_LENGTH;
  }

  get isHeaderBlank(): boolean {
    return !this.currentEditingTooltip?.header?.trim();
  }

  getHeaderCharCount(): number {
    return this.currentEditingTooltip?.header?.length ?? 0;
  }

  getTextCharCount(): number {
    return this.getVisibleTextLength(this.currentEditingTooltip?.text ?? '');
  }

  // ngx-editor's HTML output carries markup around the text the admin actually
  // typed — count against the visible text, not the raw HTML length.
  // Called from the template on every change-detection pass, so the result is
  // cached against the last input; keyed on the value itself (not updated from
  // ngModelChange) so programmatic text changes — opening, switching or
  // reverting a tooltip — are picked up too.
  private getVisibleTextLength(html: string): number {
    if (html !== this.visibleTextLengthCache?.html) {
      this.visibleTextLengthCache = { html, length: this.stripHtml(html).length };
    }
    return this.visibleTextLengthCache.length;
  }

  // Stored tooltip text is untrusted HTML (other admins, direct API calls, CSV
  // import). DOMParser builds an inert document — unlike innerHTML on an
  // element of the live document, it loads no images and runs no inline event
  // handlers (e.g. <img src=x onerror=...>).
  private stripHtml(html: string): string {
    if (!html) return '';
    return new DOMParser().parseFromString(html, 'text/html').body.textContent ?? '';
  }
}
